import React from 'react';
import { Calculator } from 'lucide-react';

interface LoanCalculatorEditorProps {
  settings: any;
  content: any;
  onSettingsChange: (settings: any) => void;
  onContentChange: (content: any) => void;
}

const LoanCalculatorEditor: React.FC<LoanCalculatorEditorProps> = ({ 
  settings, 
  content, 
  onSettingsChange, 
  onContentChange 
}) => {
  const handleSettingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' 
      ? (e.target as HTMLInputElement).checked 
      : type === 'number' 
        ? parseInt(value) 
        : value;
    
    onSettingsChange({ [name]: newValue });
  };
  
  return (
    <div className="space-y-6">
      {/* General Settings */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Genel Ayarlar</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Başlık
            </label>
            <input
              type="text"
              name="title"
              value={settings?.title || 'Kredi Hesaplama'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Kredi Türü
            </label>
            <select
              name="loanType"
              value={settings?.loanType || 'ihtiyac'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            >
              <option value="ihtiyac">İhtiyaç Kredisi</option>
              <option value="konut">Konut Kredisi</option>
              <option value="tasit">Taşıt Kredisi</option>
              <option value="kobi">Kobi Kredisi</option>
            </select>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Varsayılan Tutar
            </label>
            <input
              type="number"
              name="defaultAmount"
              value={settings?.defaultAmount || 50000}
              onChange={handleSettingChange}
              min={1000}
              step={1000}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Varsayılan Vade (Ay)
            </label>
            <input
              type="number"
              name="defaultTerm"
              value={settings?.defaultTerm || 12}
              onChange={handleSettingChange}
              min={1}
              max={120}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="showAmountSlider"
              checked={settings?.showAmountSlider !== false}
              onChange={handleSettingChange}
              className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
            />
            <span className="text-sm text-slate-600">Tutar Kaydırıcısını Göster</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="showTermSlider"
              checked={settings?.showTermSlider !== false}
              onChange={handleSettingChange}
              className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
            />
            <span className="text-sm text-slate-600">Vade Kaydırıcısını Göster</span>
          </label>
        </div>
      </div>
      
      {/* Button Settings */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Buton Ayarları</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Buton Metni
            </label>
            <input
              type="text"
              name="buttonText"
              value={settings?.buttonText || 'Kredi Hesapla'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Buton Rengi
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                name="buttonColor"
                value={settings?.buttonColor || '#F7BA45'}
                onChange={handleSettingChange}
                className="w-10 h-10 p-1 border border-slate-200 rounded-lg"
              />
              <input
                type="text"
                name="buttonColor"
                value={settings?.buttonColor || '#F7BA45'}
                onChange={handleSettingChange}
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Preview */}
      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-2">
          <Calculator size={16} className="text-[#F7BA45]" />
          <h4 className="text-sm font-medium text-slate-700">Önizleme</h4>
        </div>
        <p className="text-sm text-slate-600">
          Kredi hesaplama aracı, kullanıcıların {settings?.loanType === 'ihtiyac' ? 'ihtiyaç' : 
            settings?.loanType === 'konut' ? 'konut' : 
            settings?.loanType === 'tasit' ? 'taşıt' : 'kobi'} kredisi için 
          {settings?.defaultAmount?.toLocaleString() || '50.000'} TL tutarında ve {settings?.defaultTerm || '12'} ay vadeli 
          bir kredi hesaplaması yapmasını sağlar.
        </p>
      </div>
    </div>
  );
};

export default LoanCalculatorEditor;